import { Injectable } from '@angular/core';
import { DwQueryFilter, DwQueryFilterGroup, DwQueryHelper } from '@devwareapps/devware-cap';
import {
  ReportDatePeriodState,
  ReportFilterPresetRecord,
  ReportSavedCustomFilterState,
  ReportSavedFilterState,
  ResolvedReportFilterPreset
} from '../models/report-filter-preset.model';

export const REPORT_DATE_PERIOD_FILTER_TYPE = 'report-date-period';

@Injectable({ providedIn: 'root' })
export class ReportFilterPresetCodecService {
  resolve(record: ReportFilterPresetRecord, now: Date = new Date()): ResolvedReportFilterPreset {
    const savedGroup = this.parseGroup(record.FilterGroupJSON);
    const state = this.parseState(record.FilterStateJSON);
    const customAttributes = state.filters.map(filter => filter.attribute);
    const resolved = this.removeAttributes(savedGroup, customAttributes);

    state.filters.forEach(filter => {
      if (filter.filterTypeKey === REPORT_DATE_PERIOD_FILTER_TYPE) {
        resolved.AddFilterAnd(this.buildDatePeriodGroup(filter, now));
      }
    });

    return { record, filterGroup: resolved, filterState: state };
  }

  serialize(filterGroup: DwQueryFilterGroup, filterState?: ReportSavedFilterState): {
    FilterGroupJSON: string;
    FilterStateJSON: string;
  } {
    return {
      FilterGroupJSON: JSON.stringify(filterGroup || new DwQueryFilterGroup()),
      FilterStateJSON: filterState ? JSON.stringify(filterState) : null
    };
  }

  summarizeDatePeriod(state: ReportDatePeriodState): string {
    switch (state.mode) {
      case 'rollingDays': return `Last ${state.days} days`;
      case 'currentMonth': return 'Current month';
      case 'previousMonth': return 'Previous month';
      case 'currentQuarter': return 'Current quarter';
      case 'previousQuarter': return 'Previous quarter';
      case 'currentYear': return 'Current year';
      case 'previousYear': return 'Previous year';
      case 'exactRange': return `${state.start} - ${state.end}`;
      default: return 'Reporting period';
    }
  }

  private buildDatePeriodGroup(
    filter: ReportSavedCustomFilterState<ReportDatePeriodState>,
    now: Date
  ): DwQueryFilterGroup {
    const state = filter.value;
    const helper = new DwQueryHelper();
    const group = new DwQueryFilterGroup();

    if (state.mode === 'rollingDays') {
      if (!state.days || state.days < 1) {
        throw new Error('A rolling report period requires a positive number of days.');
      }
      group.AddFilterAnd(helper.filters.DateFilters().GreaterThanEqualDays(filter.attribute, -state.days));
      group.AddFilterAnd(helper.filters.DateFilters().LessThanEqualDays(filter.attribute, 0));
      return group;
    }

    const range = this.resolveCalendarRange(state, now);
    group.AddFilterAnd(helper.filters.GreaterThanEqual(filter.attribute, range.start));
    group.AddFilterAnd(helper.filters.LessThan(filter.attribute, range.endExclusive));
    return group;
  }

  private resolveCalendarRange(state: ReportDatePeriodState, now: Date): { start: Date; endExclusive: Date } {
    const year = now.getFullYear();
    const month = now.getMonth();
    const quarterMonth = Math.floor(month / 3) * 3;

    switch (state.mode) {
      case 'currentMonth':
        return { start: new Date(year, month, 1), endExclusive: new Date(year, month + 1, 1) };
      case 'previousMonth':
        return { start: new Date(year, month - 1, 1), endExclusive: new Date(year, month, 1) };
      case 'currentQuarter':
        return { start: new Date(year, quarterMonth, 1), endExclusive: new Date(year, quarterMonth + 3, 1) };
      case 'previousQuarter':
        return { start: new Date(year, quarterMonth - 3, 1), endExclusive: new Date(year, quarterMonth, 1) };
      case 'currentYear':
        return { start: new Date(year, 0, 1), endExclusive: new Date(year + 1, 0, 1) };
      case 'previousYear':
        return { start: new Date(year - 1, 0, 1), endExclusive: new Date(year, 0, 1) };
      case 'exactRange': {
        if (!state.start || !state.end) {
          throw new Error('An exact report period requires start and end dates.');
        }
        const start = this.parseLocalDate(state.start);
        const endExclusive = this.parseLocalDate(state.end);
        endExclusive.setDate(endExclusive.getDate() + 1);
        return { start, endExclusive };
      }
      default:
        throw new Error(`Unsupported report date period: ${state.mode}`);
    }
  }

  private removeAttributes(group: DwQueryFilterGroup, attributes: string[]): DwQueryFilterGroup {
    const result = Object.assign(new DwQueryFilterGroup(), group);
    result.Filters = (group.Filters || [])
      .map(filter => this.removeAttributeFilter(filter, attributes))
      .filter(filter => !!filter);
    return result;
  }

  private removeAttributeFilter(filter: DwQueryFilter, attributes: string[]): DwQueryFilter {
    const childFilters = (filter as DwQueryFilterGroup).Filters;
    if (Array.isArray(childFilters)) {
      const group = Object.assign(new DwQueryFilterGroup(), filter);
      group.Filters = childFilters
        .map(child => this.removeAttributeFilter(child, attributes))
        .filter(child => !!child);
      return group.Filters.length ? group : null;
    }
    return attributes.indexOf(filter.Attr) >= 0 ? null : filter;
  }

  private parseGroup(value: string): DwQueryFilterGroup {
    const parsed = value ? JSON.parse(value) : {};
    const group = Object.assign(new DwQueryFilterGroup(), parsed);
    group.Filters = parsed.Filters || [];
    return group;
  }

  private parseState(value?: string): ReportSavedFilterState {
    if (!value) {
      return { schemaVersion: 1, filters: [] };
    }
    const state = JSON.parse(value) as ReportSavedFilterState;
    if (state.schemaVersion !== 1 || !Array.isArray(state.filters)) {
      throw new Error('Saved report filter state is invalid or unsupported.');
    }
    return state;
  }

  private parseLocalDate(value: string): Date {
    const parts = value.split('-').map(part => Number(part));
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

}
